import { defineStore } from "pinia";
import { IPSLot } from "../../models/sanitaire/psLot"
import { IPSLotFilter } from "../../models/filters/sanitaire/psLotFilter"
import { IPSLotForm } from "../../models/forms/sanitaire/psLotForm";
import { ServiceOperationResult } from "../../models/api/serviceOperationResult";
import DataOperationError from "../../models/api/dataOperationError";
import { IDataOperationResult } from "../../models/api/dataOperationResult";
import PSLotService from "../../services/sanitaire/PSLotService";
import signalRManager from "../../helper/signalRManager";
import IPayloadStore from "../../models/api/payloadStore";

export const usePSLotStore = defineStore("psLots", {
    state: () => ({
        datas: [] as Array<IPSLot>,
        current : {} as IPSLot,
        itemForm : {} as IPSLotForm,
        isLoading: false as boolean,
    }),
    getters: {

    },
    actions: {

        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns
         */
        async load(filter: IPSLotFilter) {
            this.isLoading = true;
            let result = await PSLotService.getAll<IPSLotFilter,IPSLot>(filter);
            this.isLoading = false;
            this.datas = [];
            if (!result.error) {
                var resultData = result.data as IPSLot[];
                this.datas = resultData;
            }
            return result;
        },

        /**
         * Récupération d'un psLot par son identifiant
         * @param id
         * @returns
         */
        async get(id: number) {
            let result = await PSLotService.getById<IPSLot>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as IPSLot;
                // on le stock dans le current puis dans le form
                this.current = result.data as IPSLot;
                this.itemForm = this.toForm(result.data as IPSLot);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as IPSLot);
            return this.current;
        },

        /**
         * Mise à jour ou création d'un objet
         * @param maj
         * @returns
         */
        async save(maj:boolean = true, isPatch:boolean = false)
        {
            let result = {} as ServiceOperationResult<IPSLot|IDataOperationResult<IPSLot>>;
            if ( this.itemForm.id > 0) {
                result = await PSLotService.update(this.itemForm);

                if ( !result.error)
                {
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            // Mutate en place pour preserver la reference partagee detenue
                            // par les boars (boars.store.ts:438). Un remplacement creerait
                            // un nouvel objet et le PDM continuerait d'afficher l'ancien code.
                            Object.assign(this.datas[index], result.data);
                            this.current = this.datas[index];
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = "Item non trouvé dans le store";
                            return new ServiceOperationResult<IDataOperationResult<IPSLot>>(true, {errors: error});
                        }
                    } else {
                        this.current = result.data as IPSLot;
                    }
                }
            } else {
                result = await PSLotService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IPSLot;
                    this.datas.push(this.current);
                }

            }
            return result;
        },

        /**
         * Delete de l'objet
         * @returns
         */
        async delete() {
            let result = await PSLotService.delete<IPSLot>(this.current.id);

            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == this.current.id);
                if (index >= 0) {
                    // On remove l'élément des données
                    this.datas.splice(index, 1);
                }

                this.current = this.newItem();
                this.itemForm = this.newForm();
            }
            return result;
        },

        /**
         * Transformation de l'objet en formulaire
         * @param item PSLot
         * @returns
         */
        toForm(item: IPSLot): IPSLotForm {
            const form = {} as IPSLotForm;
            form.id = item.id;
            form.code = item.code;
            this.itemForm = form;
            return form;
        },

        /**
         * Permet d'initialiser un objet vide pour le formulaire
         * @returns
         */
        newForm(): IPSLotForm {
            this.itemForm = {
                id: 0,
                code: ""
            } as IPSLotForm;
            return this.itemForm;
        },

        /**
         *  Permet d'initialiser un objet vide pour les referentiels
         * @returns
         */
        newItem(): IPSLot {

            this.current = {
                id: 0,
                code: ""
            } as IPSLot;

            this.itemForm = this.toForm(this.current);
            return this.current;
        },
    }
})

signalRManager.connection.on("MajPSLot", (payload: IPayloadStore) => {
    const store = usePSLotStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                store.datas.push(payload.data as unknown as IPSLot);
            }
            break;
        }
        case "Update": {
            const existing = store.datas.find((a) => a.id == payload.data?.id);
            if (existing) {
                // Mutate in-place : les boars detiennent une reference partagee vers cet objet
                // (boars.store.ts:438 : psLotMap.get(boar.psLotId)). Un splice creerait un nouvel
                // objet et le PDM continuerait d'afficher l'ancien code.
                Object.assign(existing, payload.data);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
